import { useEffect, useMemo, useState } from "react";

/** Flatten research card sections into streamable tokens. */
export function sectionsToTokens(sections) {
  const tokens = [];
  (sections || []).forEach((section) => {
    tokens.push({ type: "h3", text: section.title });
    (section.bullets || []).forEach((bullet) => {
      tokens.push({ type: "li", text: bullet });
    });
  });
  return tokens;
}

/**
 * Character-by-character mock stream (LLM-style).
 * @param {unknown} streamKey — change to restart (e.g. run counter)
 */
export function useCharacterStream(fullText, streamKey, options = {}) {
  const { charDelay = 10, startDelay = 0, enabled = true } = options;
  const [length, setLength] = useState(enabled && streamKey ? 0 : fullText.length);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!enabled || !streamKey) {
      setLength(fullText.length);
      setStarted(false);
      return undefined;
    }
    setLength(0);
    setStarted(false);
    const startId = setTimeout(() => setStarted(true), startDelay);
    return () => clearTimeout(startId);
  }, [streamKey, fullText, enabled, startDelay]);

  useEffect(() => {
    if (!started || !enabled || !streamKey) return undefined;
    if (length >= fullText.length) return undefined;
    const jitter = Math.random() * 6;
    const id = setTimeout(() => setLength((n) => n + 1), charDelay + jitter);
    return () => clearTimeout(id);
  }, [started, length, fullText.length, charDelay, enabled, streamKey]);

  const isStreaming = enabled && !!streamKey && length < fullText.length;
  return {
    text: fullText.slice(0, length),
    isStreaming,
    isDone: !isStreaming,
  };
}

/**
 * Stream structured research tokens (h3 + bullets) in order.
 */
export function useStructuredStream(tokens, streamKey, options = {}) {
  const { charDelay = 10, startDelay = 0, enabled = true } = options;
  const [tokenIndex, setTokenIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!enabled || !streamKey) {
      setTokenIndex(tokens.length);
      setCharIndex(0);
      setActive(false);
      return undefined;
    }
    setTokenIndex(0);
    setCharIndex(0);
    setActive(false);
    const id = setTimeout(() => setActive(true), startDelay);
    return () => clearTimeout(id);
  }, [streamKey, tokens, enabled, startDelay]);

  useEffect(() => {
    if (!active || !enabled || !streamKey) return undefined;
    const current = tokens[tokenIndex];
    if (!current) return undefined;

    if (charIndex < current.text.length) {
      const id = setTimeout(
        () => setCharIndex((c) => c + 1),
        charDelay + Math.random() * 6,
      );
      return () => clearTimeout(id);
    }

    if (tokenIndex < tokens.length - 1) {
      const id = setTimeout(() => {
        setTokenIndex((t) => t + 1);
        setCharIndex(0);
      }, 40);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [active, tokenIndex, charIndex, tokens, charDelay, enabled, streamKey]);

  const rendered = useMemo(() => {
    if (!enabled || !streamKey) return tokens;
    return tokens.map((token, i) => {
      if (i < tokenIndex) return { ...token, text: token.text };
      if (i === tokenIndex) return { ...token, text: token.text.slice(0, charIndex) };
      return null;
    }).filter(Boolean);
  }, [tokens, tokenIndex, charIndex, streamKey, enabled]);

  const isStreaming =
    enabled && !!streamKey && (tokenIndex < tokens.length - 1 ||
      (tokens[tokenIndex] && charIndex < tokens[tokenIndex].text.length));

  return { tokens: rendered, isStreaming, isDone: !isStreaming };
}

/** Stream list bullets one after another. */
export function useBulletListStream(bullets, streamKey, options = {}) {
  const { charDelay = 10, startDelay = 0, enabled = true } = options;
  const [bulletIndex, setBulletIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!enabled || !streamKey) {
      setBulletIndex(bullets.length);
      setCharIndex(0);
      setActive(false);
      return undefined;
    }
    setBulletIndex(0);
    setCharIndex(0);
    setActive(false);
    const id = setTimeout(() => setActive(true), startDelay);
    return () => clearTimeout(id);
  }, [streamKey, bullets, enabled, startDelay]);

  useEffect(() => {
    if (!active || !enabled || !streamKey) return undefined;
    const current = bullets[bulletIndex] || "";
    if (charIndex < current.length) {
      const id = setTimeout(
        () => setCharIndex((c) => c + 1),
        charDelay + Math.random() * 6,
      );
      return () => clearTimeout(id);
    }
    if (bulletIndex < bullets.length - 1) {
      const id = setTimeout(() => {
        setBulletIndex((b) => b + 1);
        setCharIndex(0);
      }, 50);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [active, bulletIndex, charIndex, bullets, charDelay, enabled, streamKey]);

  const displayed = bullets.map((bullet, i) => {
    if (!enabled || !streamKey) return bullet;
    if (i < bulletIndex) return bullet;
    if (i === bulletIndex) return bullet.slice(0, charIndex);
    return null;
  }).filter((line) => line !== null);

  const isStreaming =
    enabled &&
    !!streamKey &&
    (bulletIndex < bullets.length - 1 ||
      (bullets[bulletIndex] && charIndex < bullets[bulletIndex].length));

  return { bullets: displayed, isStreaming, isDone: !isStreaming };
}
